/**
 * Audio Output Diagnostics
 *
 * On-screen probe answering a single question: can this device offer a
 * "loudspeaker" button at all?
 *
 * Choosing an audio output from the web needs two things that mobile browsers
 * often lack: the OS must expose the speaker and the earpiece as *separate*
 * `audiooutput` devices, and the browser must implement setSinkId. Android
 * commonly routes audio itself and exposes nothing to pick from, in which case no
 * amount of application code can build the button.
 *
 * Results are rendered rather than logged because this is meant to be read on the
 * phone itself, where the console is out of reach.
 *
 * Reached via `?audiodiag=1`.
 */

import { useState } from 'react';

interface OutputDevice {
  deviceId: string;
  label: string;
  groupId: string;
}

type SwitchResult = { deviceId: string; ok: boolean; detail: string };

/** Feature flags read straight off the prototypes, so they reflect this browser. */
function capabilities() {
  const AudioCtx =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  return {
    elementSetSinkId: typeof (HTMLMediaElement.prototype as HTMLMediaElement & {
      setSinkId?: unknown;
    }).setSinkId === 'function',
    audioContextSetSinkId:
      !!AudioCtx &&
      typeof (AudioCtx.prototype as AudioContext & { setSinkId?: unknown }).setSinkId === 'function',
    selectAudioOutput:
      typeof (navigator.mediaDevices as MediaDevices & { selectAudioOutput?: unknown })
        ?.selectAudioOutput === 'function',
  };
}

export function AudioOutputDiagnostics() {
  const [devices, setDevices] = useState<OutputDevice[] | null>(null);
  const [inputCount, setInputCount] = useState<number | null>(null);
  const [micGranted, setMicGranted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [switches, setSwitches] = useState<SwitchResult[]>([]);
  const [toneOn, setToneOn] = useState(false);

  const caps = capabilities();

  /**
   * Device labels stay empty until a microphone permission has been granted, and
   * some browsers hide output devices entirely until then — so ask first, then list.
   */
  const probe = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicGranted(true);
    } catch (e) {
      setMicGranted(false);
      setError(`Micro refusé : ${e instanceof Error ? e.message : String(e)}`);
    }

    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setInputCount(all.filter((d) => d.kind === 'audioinput').length);
      setDevices(
        all
          .filter((d) => d.kind === 'audiooutput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label, groupId: d.groupId }))
      );
    } catch (e) {
      setError(`enumerateDevices a échoué : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /**
   * Plays a continuous tone through an AudioContext. Enumeration alone can lie —
   * the only proof a switch works is hearing the sound move.
   */
  const startTone = async () => {
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      const ctx = new AudioCtx();
      await ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      (window as Window & { __diagCtx?: AudioContext }).__diagCtx = ctx;
      setToneOn(true);
    } catch (e) {
      setError(`Son de test impossible : ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  /** Attempts to move the running tone to one device, and records what happened. */
  const trySwitch = async (deviceId: string) => {
    const ctx = (window as Window & { __diagCtx?: AudioContext }).__diagCtx as
      | (AudioContext & { setSinkId?: (id: string) => Promise<void> })
      | undefined;
    if (!ctx) {
      setError('Lance le son de test avant de changer de sortie.');
      return;
    }
    if (typeof ctx.setSinkId !== 'function') {
      setSwitches((s) => [...s, { deviceId, ok: false, detail: 'AudioContext.setSinkId absent' }]);
      return;
    }
    try {
      await ctx.setSinkId(deviceId);
      setSwitches((s) => [...s, { deviceId, ok: true, detail: 'accepté — le son a-t-il bougé ?' }]);
    } catch (e) {
      setSwitches((s) => [
        ...s,
        { deviceId, ok: false, detail: e instanceof Error ? e.message : String(e) },
      ]);
    }
  };

  const Row = ({ k, v }: { k: string; v: string }) => (
    <div className="flex justify-between gap-3 border-b border-white/10 py-1.5">
      <span className="text-white/60">{k}</span>
      <span className="text-white text-right break-all">{v}</span>
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-slate-900 text-white p-4 text-sm overflow-y-auto">
      <h1 className="text-lg font-bold mb-1">Diagnostic sortie audio</h1>
      <p className="text-white/50 mb-4 text-xs">
        Objectif : savoir si un bouton haut-parleur est possible sur ce téléphone.
      </p>

      <div className="mb-4">
        <Row k="setSinkId (élément audio)" v={caps.elementSetSinkId ? '✅ oui' : '❌ non'} />
        <Row k="setSinkId (AudioContext)" v={caps.audioContextSetSinkId ? '✅ oui' : '❌ non'} />
        <Row k="selectAudioOutput()" v={caps.selectAudioOutput ? '✅ oui' : '❌ non'} />
        <Row k="Navigateur" v={navigator.userAgent} />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={probe} className="px-3 py-2 bg-nomade-accent rounded font-medium">
          1. Lister les sorties
        </button>
        <button
          onClick={startTone}
          disabled={toneOn}
          className="px-3 py-2 bg-white/15 rounded font-medium disabled:opacity-40"
        >
          {toneOn ? '🔊 son en cours' : '2. Jouer un son de test'}
        </button>
      </div>

      {error && (
        <p className="mb-4 p-2 bg-red-500/20 border border-red-500/40 rounded text-red-200 text-xs">
          {error}
        </p>
      )}

      {devices !== null && (
        <div className="mb-4">
          <Row k="Micro autorisé" v={micGranted ? '✅ oui' : '❌ non'} />
          <Row k="Entrées audio détectées" v={String(inputCount ?? '—')} />
          <Row k="Sorties audio détectées" v={String(devices.length)} />

          {devices.length === 0 ? (
            <p className="mt-3 p-2 bg-amber-500/20 border border-amber-500/40 rounded text-amber-100 text-xs">
              Aucune sortie listée : ce navigateur ne laisse pas choisir la sortie audio.
              Un bouton haut-parleur est impossible ici — c'est le système qui décide.
            </p>
          ) : (
            <div className="mt-3 space-y-2">
              {devices.map((d, i) => (
                <div key={d.deviceId || i} className="p-2 bg-white/5 rounded">
                  <div className="font-medium">{d.label || '(sans nom)'}</div>
                  <div className="text-white/40 text-xs break-all">id : {d.deviceId || '(vide)'}</div>
                  <button
                    onClick={() => trySwitch(d.deviceId)}
                    className="mt-2 px-2 py-1 bg-white/15 rounded text-xs"
                  >
                    3. Envoyer le son ici
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {switches.length > 0 && (
        <div>
          <h2 className="font-bold mb-1">Tentatives de bascule</h2>
          {switches.map((s, i) => (
            <Row key={i} k={s.ok ? '✅' : '❌'} v={`${s.deviceId.slice(0, 12)}… — ${s.detail}`} />
          ))}
        </div>
      )}
    </div>
  );
}
